/**
 * Smoothly scroll an element into view and highlight it with the
 * `update-focus-highlight` treatment defined in `app/styles/global.css`.
 *
 * Shared by the product-update announcements panel and any in-app empty
 * states / CTAs that want to direct the user to a specific control.
 */
let highlightedElement: HTMLElement | null = null;
let removeHighlightInteractionListeners: (() => void) | null = null;
const PENDING_FOCUS_STORAGE_KEY = "moneytor:pending-focus-target";
const PROGRAMMATIC_SCROLL_GRACE_MS = 900;

type PendingFocusTarget = {
  focusId: string;
  innerFocusId?: string;
};

function clearHighlightedElement() {
  highlightedElement?.classList.remove("update-focus-highlight");
  highlightedElement = null;
  removeHighlightInteractionListeners?.();
  removeHighlightInteractionListeners = null;
}

function clearHighlightOnNextUserInteraction() {
  removeHighlightInteractionListeners?.();

  const clear = () => clearHighlightedElement();
  const clearOnScroll = () => {
    if (Date.now() < ignoreScrollUntil) return;
    clearHighlightedElement();
  };
  const options: AddEventListenerOptions = { capture: true, once: true };
  const scrollOptions: AddEventListenerOptions = { capture: true, passive: true };
  const target = document;
  const ignoreScrollUntil = Date.now() + PROGRAMMATIC_SCROLL_GRACE_MS;

  target.addEventListener("pointerdown", clear, options);
  target.addEventListener("mousedown", clear, options);
  target.addEventListener("touchstart", clear, options);
  target.addEventListener("click", clear, options);
  target.addEventListener("wheel", clear, options);
  target.addEventListener("touchmove", clear, options);
  target.addEventListener("scroll", clearOnScroll, scrollOptions);

  removeHighlightInteractionListeners = () => {
    target.removeEventListener("pointerdown", clear, options);
    target.removeEventListener("mousedown", clear, options);
    target.removeEventListener("touchstart", clear, options);
    target.removeEventListener("click", clear, options);
    target.removeEventListener("wheel", clear, options);
    target.removeEventListener("touchmove", clear, options);
    target.removeEventListener("scroll", clearOnScroll, scrollOptions);
  };
}

export function highlightElement(el: HTMLElement) {
  const scrollBlock = (el.dataset.focusScrollBlock as
  ScrollLogicalPosition |
  undefined);
  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  if (!el.hasAttribute("data-focus-no-scroll")) {
    el.scrollIntoView({
      behavior: reducedMotion ? "auto" : "smooth",
      block: scrollBlock ?? "center"
    });
  }
  clearHighlightedElement();
  el.classList.add("update-focus-highlight");
  if (!el.matches("button, a, input, select, textarea, [tabindex]")) {
    el.setAttribute("tabindex", "-1");
  }
  el.focus({ preventScroll: true });
  highlightedElement = el;
  clearHighlightOnNextUserInteraction();
}

type ScrollToAndHighlightOptions = {
  onHighlighted?: (el: HTMLElement) => void;
  initialDelayMs?: number;
  retryDelayMs?: number;
  innerDelayMs?: number;
  attempts?: number;
};

export function savePendingFocusTarget(
focusId: string,
innerFocusId?: string)
{
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(
      PENDING_FOCUS_STORAGE_KEY,
      JSON.stringify({ focusId, innerFocusId })
    );
  } catch {

    // sessionStorage can be unavailable in private mode or strict browser modes.
  }}

export function consumePendingFocusTarget(): PendingFocusTarget | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(PENDING_FOCUS_STORAGE_KEY);
    window.sessionStorage.removeItem(PENDING_FOCUS_STORAGE_KEY);
    if (!raw) return null;

    const parsed = (JSON.parse(raw) as Partial<PendingFocusTarget>);
    if (typeof parsed.focusId !== "string" || parsed.focusId.length === 0) {
      return null;
    }

    return {
      focusId: parsed.focusId,
      innerFocusId:
      typeof parsed.innerFocusId === "string" ?
      parsed.innerFocusId :
      undefined
    };
  } catch {
    return null;
  }
}

function isVisibleElement(el: HTMLElement) {
  const rect = el.getBoundingClientRect();
  const style = window.getComputedStyle(el);

  return (
    rect.width > 0 &&
    rect.height > 0 &&
    style.display !== "none" &&
    style.visibility !== "hidden");

}

function selectorValue(value: string) {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function findFocusElement(focusId: string) {
  const matchingElements = [
  ...Array.from(
    document.querySelectorAll<HTMLElement>(`[id="${selectorValue(focusId)}"]`)
  ),
  ...Array.from(
    document.querySelectorAll<HTMLElement>(
      `[data-app-progress-focus-id="${selectorValue(focusId)}"]`
    )
  )];


  return matchingElements.find(isVisibleElement) ?? matchingElements[0] ?? null;
}

/**
 * Locate `focusId` in the DOM and highlight it directly. When an
 * `innerFocusId` is already visible, highlight that target instead without
 * triggering the control that reveals it.
 *
 * Same behaviour previously inlined in `ProductUpdateItem.tsx`.
 */
export function scrollToAndHighlight(
focusId: string,
innerFocusId?: string,
options: ScrollToAndHighlightOptions = {})
{
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const initialDelayMs = options.initialDelayMs ?? 400;
  const retryDelayMs = options.retryDelayMs ?? 300;
  const innerDelayMs = options.innerDelayMs ?? 180;
  const attempts = options.attempts ?? 20;
  let openedFocusElement = false;

  const findAndHighlight = (attemptsLeft: number) => {
    const el = findFocusElement(focusId);
    if (!el) {
      if (attemptsLeft > 0) {
        setTimeout(() => findAndHighlight(attemptsLeft - 1), retryDelayMs);
      }
      return;
    }

    if (innerFocusId) {
      const inner = findFocusElement(innerFocusId);
      if (inner) {
        highlightElement(inner);
        options.onHighlighted?.(inner);
        return;
      }

      if (el.hasAttribute("data-open-on-focus") && !openedFocusElement) {
        openedFocusElement = true;
        highlightElement(el);
        el.click();
        setTimeout(() => findAndHighlight(attemptsLeft), innerDelayMs);
        return;
      }
    }

    highlightElement(el);
    options.onHighlighted?.(el);
  };

  window.requestAnimationFrame(() => {
    setTimeout(() => findAndHighlight(attempts), initialDelayMs);
  });
}